import React, {useEffect, useState} from 'react';
import {ArrayField, UseFormMethods, FieldError} from 'react-hook-form';
import {FacilityRoleDto} from 'server-openapi';
import {DateInput} from '../../../../kit/Forms/DateInput';
import {FormGroup} from '../../../../kit/Forms/FormGroup';
import {SelectOption, Select} from '../../../../kit/Forms/Select';
import {Grid} from '../../../../kit/Grid';
import {FacilityRoleAccessDto, UserDetailsFormValues} from '../UserDetailsPage';
import styled, { css } from "styled-components";

const LabelText = styled.span<{ $disabled?: boolean }>`
  margin-left: 20px;
  color: black;

  ${(props) =>
    props.$disabled && css`
    color: rgb(84, 84, 84);
  `}
`;

const DisabledFieldBackground = styled.div<{ $disabled?: boolean }>`
  width: 100%;

  ${(props) =>
    props.$disabled && css`
      background-color: #e0e0e0;

      input,
      select,
      textarea,
      [class*='control'],
      [class*='Control'] {
        background-color: #e0e0e0 !important;
      }
    `}
`;

const StatusBackground = styled(DisabledFieldBackground)`
  min-height: 50px;
  display: flex;
  align-items: center;
  
`;

interface IProps {
  field: Partial<ArrayField<FacilityRoleAccessDto, 'id'>>;
  index: number;
  form: UseFormMethods<UserDetailsFormValues>;
  facilityRoleOptions: SelectOption<FacilityRoleDto>[];
  disabled?: boolean;
  roleDisabled?: boolean;
}

export const UserFacilityRolesDetails = (props: IProps) => {
  const {field, index, form, facilityRoleOptions, disabled, roleDisabled} = props;
  const [isCreatedWithinThirtyMinutesAgo, setIsCreatedWithinThirtyMinutesAgo] = useState(false);
  const [isDisabledExpired, setIsDisabledExpired] = useState(false);
  const [expirydate, setExpiryDate] = useState<Date>();

  const addFiveMinuteToCurrentTime = (): Date => {
    const currentTime = new Date();
    currentTime.setMinutes(currentTime.getMinutes() + 5);
    return currentTime;
  }

  const checkStartDate = () => {
    const thirtyMinutesAgo = Date.now() - (1000 * 30 * 60);

    if (field.startDate && field.startDate.getTime() < thirtyMinutesAgo) {
      setIsCreatedWithinThirtyMinutesAgo(true);
    }
  }

  const checkExpiryDate = () => {
    const todayDate = new Date();

    if (field.expiryDate && field.expiryDate < todayDate) {
      setIsDisabledExpired(true);
    }
  }

  const validateStartDate = (startDate: Date | undefined) => {
    const startTime = startDate?.getTime();
    const todayDate = new Date();
    const todayTime = todayDate.getTime();
    // when adding new role, start time is automatically set to todayDate/Time () hence causing validation error. Here we give 10 minutes window for an error-free role assignment
    const tenMinutesAgo = Date.now() - (1000 * 10 * 60);

    if (!startDate) {
      return 'Start date is required';
    }
    const currentStart = form.getValues().facilityRoleAccess[index].startDate;

    if (!props.field.isNew && currentStart.getTime() < tenMinutesAgo && startTime !== currentStart.getTime()) {
      return "You cannot alter the start date of a current role assignment."
    }
    if (startTime && startDate && startDate < todayDate && startTime < todayTime && startTime < tenMinutesAgo && props.field.isNew) { // it only validates the newly added role. Old roles/start dates won't be validated.
      return 'Start date cannot be earlier than today\'s date';
    }
  };

  const validateExpiryDate = (expiryDate: Date | undefined) => {
    const todayDate = new Date();
    const todayTime = todayDate.getTime();
    const currentExpiryDate = form.getValues().facilityRoleAccess[index].expiryDate;
    const expiryTime = expiryDate ? expiryDate?.getTime() : null;
    const startDate = form.getValues().facilityRoleAccess[index].startDate;
    const startTime = form.getValues().facilityRoleAccess[index].startDate ? form.getValues().facilityRoleAccess[index].startDate.getTime() : undefined;

    // Don't validate if expiry date is invalid (lovely!)
    // Reason: For example facility manager like how a nurse works and re-hires him for temp permanent then
    // facility manager will adjust his expiry date to non expiry.
    // We must not let an invalid date to enter into the system in the future and managers have to adhere to professional practices.
    if (expiryDate?.toString() === 'Invalid Date') {
      return;
    }

    if (expiryTime && expiryDate && startTime && startDate && expiryDate < startDate && expiryTime < startTime && !isDisabledExpired) {
      return 'Expiry date cannot be earlier than start date';
    }
    if (!props.field.isNew && expiryDate && expiryDate !== currentExpiryDate && expiryDate < todayDate && !isDisabledExpired) { // it is for old and currently null expiry dates. Old expiry dates with value are already read only (greyed out).
      return 'Expiry date cannot be earlier than today\'s date';
    }
    if (expiryDate && expiryTime && todayDate && todayTime && expiryDate < todayDate && props.field.isNew) { // it only validates the newly added role. Old roles/expiry dates won't be validated.
      return 'Expiry date cannot be earlier than today\'s date';
    }
    if (startTime && startDate && expiryTime && expiryDate && startDate > expiryDate && startTime > expiryTime && !isDisabledExpired) {
      return 'Start date cannot be later than expiry date';
    }
  }

  useEffect(() => {
    checkStartDate();
    checkExpiryDate();
    setExpiryDate(addFiveMinuteToCurrentTime);
  }, []);


  return (
    <Grid colsTemplate="2fr 1fr 1fr 0.6fr">
      <FormGroup label="Title" fullWidth
                 error={(form.errors.facilityRoleAccess?.[index]?.facilityRole as FieldError)?.message}>
        <DisabledFieldBackground
            $disabled={!field.active || roleDisabled || disabled || isCreatedWithinThirtyMinutesAgo || isDisabledExpired}>
          <Select
            fullWidth
            name={`facilityRoleAccess.${index}.facilityRole`}
            options={facilityRoleOptions}
            disabled={!field.active || roleDisabled || disabled || isCreatedWithinThirtyMinutesAgo || isDisabledExpired}
            value={
                facilityRoleOptions.find((facilityRole) => facilityRole.value.hsId === field.facilityRole?.hsId)?.value ?? {'No FacilityRoleOption for field.facilityRole?.hsId': 'No FacilityRoleOption for field.facilityRole?.hsId'}
            }
            rules={{
              validate: (facilityRole: FacilityRoleDto) => {
                if (facilityRole === undefined || Object.keys(facilityRole).length <= 1) {
                  return 'The facilityRole field is required';
                }
              },
            }}
          />
        </DisabledFieldBackground>
      </FormGroup>
      <FormGroup label="Start Date" isRequired fullWidth error={form.errors.facilityRoleAccess?.[index]?.startDate?.message}>
        <DisabledFieldBackground
            $disabled={!field.active || roleDisabled || disabled || isCreatedWithinThirtyMinutesAgo || isDisabledExpired}>
          <DateInput
            dateTime
            name={`facilityRoleAccess.${index}.startDate`}
            value={field.startDate}
            fullWidth
            rules={{
              validate: (value) => {
                return validateStartDate(value);
              }
            }}
            required
            disabled={!field.active || disabled || isCreatedWithinThirtyMinutesAgo || isDisabledExpired}
          />
        </DisabledFieldBackground>
      </FormGroup>
      <FormGroup label="Expiry Date" fullWidth error={form.errors.facilityRoleAccess?.[index]?.expiryDate?.message}>
        <DisabledFieldBackground
            $disabled={!field.active || disabled || isDisabledExpired}>
          <DateInput
            dateTime
            name={`facilityRoleAccess.${index}.expiryDate`}
            onClick={() => {
              if (!field.expiryDate) {
                form.setValue(`facilityRoleAccess.${index}.expiryDate`, expirydate);
              }
            }}
            value={field.expiryDate ?? undefined}
            rules={{
              validate: (value) => {
                return validateExpiryDate(value);
              }
            }}
            fullWidth
            disabled={!field.active || disabled || isDisabledExpired}
          />
        </DisabledFieldBackground>
      </FormGroup>
      <FormGroup label="&nbsp;">
        <StatusBackground $disabled={ !field.active || disabled || isDisabledExpired}>
          <LabelText
            $disabled={!field.active || disabled || isDisabledExpired} >
            {field.active && !isDisabledExpired
                ? 'Active'
                : 'Inactive'}
          </LabelText>
        </StatusBackground>
      </FormGroup>
    </Grid>
  );
}
